import { ReactElement, useMemo, useRef, useState } from 'react';
import {
    UIIcon,
    UIMenu,
    UIMenuDivider,
    UIMenuItem,
    UIPopover,
    UIPressableAreaButtonLess,
    UIPropoverRef,
    useUIConfirmationModal
} from '@/ui';
import { charcoal, red } from '@/ui/styles';
import fallIcon from '../assets/images/fall.png';
import tiltIcon from '../assets/images/tilt.png';
import geofenceIcon from '../assets/images/geofence.png';
import { Device } from '@/api';
import {
    useCancelDeviceFallAlertAction,
    useCancelDeviceGeoAlertAction,
    useCancelDevicePowerOffAction,
    useCancelDeviceRebootAction,
    useCancelDeviceTiltAlertAction,
    useCancelDeviceWifiAction,
    usePowerOffDeviceAction,
    useRebootDeviceAction
} from '../actions';
import { useDevicesTranslation } from '../hooks';
import { useBulkAlertLicenses } from '../queries';
import { deriveDevicePendingChanges } from '../util/device-pending-changes';
import { BulkGeofenceModal } from './bulk-geofence-modal';
import { BulkWifiModal } from './bulk-wifi-modal';
import { FallAlertModal } from './fall-alert-modal';
import { TestCallDeviceModal } from './test-call-device-modal';
import { TiltAlertModal } from './tilt-alert-modal';
import styles from './device-row-actions-menu.module.scss';

/** A pending change on this device that can be cancelled. */
type CancelEntry = {
    key: string;
    label: string;
    run: () => Promise<unknown>;
};

/**
 * The per-row ellipsis menu: update/test plus quick config (fall, tilt,
 * geofence, wifi), reboot/power-off, and cancel for any change currently
 * pending on the device. Reboot/power-off/cancel confirm first.
 */
export function DeviceRowActionsMenu(props: {
    device: Device;
    companyId: string;
    /** Opens the device's Update modal, which the row owns (NoTeamCell uses it too). */
    onUpdate: () => void;
}): ReactElement {
    const { device, companyId, onUpdate } = props;
    const { t } = useDevicesTranslation();
    const popoverRef = useRef<UIPropoverRef>(null);
    const confirmationModal = useUIConfirmationModal();

    const [isTestOpen, setIsTestOpen] = useState(false);
    const [isFallOpen, setIsFallOpen] = useState(false);
    const [isTiltOpen, setIsTiltOpen] = useState(false);
    const [isGeoOpen, setIsGeoOpen] = useState(false);
    const [isWifiOpen, setIsWifiOpen] = useState(false);

    const deviceId = device.id;
    const { rebootDevice } = useRebootDeviceAction({ deviceId });
    const { powerOffDevice } = usePowerOffDeviceAction({ deviceId });
    const { cancelDeviceReboot } = useCancelDeviceRebootAction({ deviceId });
    const { cancelDevicePowerOff } = useCancelDevicePowerOffAction({
        deviceId
    });
    const { cancelDeviceFallAlert } = useCancelDeviceFallAlertAction({
        deviceId
    });
    const { cancelDeviceTiltAlert } = useCancelDeviceTiltAlertAction({
        deviceId
    });
    const { cancelDeviceGeoAlert } = useCancelDeviceGeoAlertAction({
        deviceId
    });
    const { cancelDeviceWifi } = useCancelDeviceWifiAction({ deviceId });

    const deviceIds = useMemo(() => [deviceId], [deviceId]);
    const licenses = useBulkAlertLicenses({ companyId, deviceIds });

    // Disables a fall/tilt/geofence menu item when the feature can't be enabled
    // on this device: the plan has no licences for it (limit 0), or all licences
    // are in use and this device would need a new one. A device that already
    // uses the feature stays enabled so it can be reconfigured or turned off.
    // `unavailableText` is a whole sentence per feature rather than a template
    // with the feature name slotted in: a translator needs the article and
    // gender to agree with the noun, which an interpolated label cannot supply.
    function featureLockProps(
        availability: { limit: number; free: number; toEnable: number },
        unavailableText: string
    ): { disabled?: boolean; disabledTooltip?: string } {
        if (availability.limit === 0) {
            return {
                disabled: true,
                disabledTooltip: unavailableText
            };
        }
        if (availability.free === 0 && availability.toEnable > 0) {
            return {
                disabled: true,
                disabledTooltip: t('shared.devices.feature_limit_reached')
            };
        }
        return {};
    }

    const cancelEntries = useMemo<CancelEntry[]>(() => {
        const pending = deriveDevicePendingChanges(device);
        // One whole sentence per feature rather than "Cancel {{label}}" with a
        // translated noun slotted in: Spanish agrees the article with that noun
        // ("Cancelar el reinicio" vs "Cancelar la detección de caídas"), which a
        // translator cannot supply from a template.
        const entries: CancelEntry[] = [];

        if (pending.fall) {
            entries.push({
                key: 'fall',
                label: t('components.device_row_actions_menu.cancel_fall'),
                run: cancelDeviceFallAlert
            });
        }
        if (pending.tilt) {
            entries.push({
                key: 'tilt',
                label: t('components.device_row_actions_menu.cancel_tilt'),
                run: cancelDeviceTiltAlert
            });
        }
        pending.geoSlots.forEach((slot) => {
            entries.push({
                key: `geo-${slot}`,
                label: t('components.device_row_actions_menu.cancel_geofence', {
                    number: slot + 1
                }),
                run: () => cancelDeviceGeoAlert({ slot })
            });
        });
        pending.wifiSlots.forEach((slot) => {
            entries.push({
                key: `wifi-${slot}`,
                label: t('components.device_row_actions_menu.cancel_wifi', {
                    number: slot + 1
                }),
                run: () => cancelDeviceWifi({ slot })
            });
        });
        if (pending.reboot) {
            entries.push({
                key: 'reboot',
                label: t('components.device_row_actions_menu.cancel_reboot'),
                run: cancelDeviceReboot
            });
        }
        if (pending.powerOff) {
            entries.push({
                key: 'power_off',
                label: t('components.device_row_actions_menu.cancel_power_off'),
                run: cancelDevicePowerOff
            });
        }

        return entries;
    }, [
        device,
        t,
        cancelDeviceFallAlert,
        cancelDeviceTiltAlert,
        cancelDeviceGeoAlert,
        cancelDeviceWifi,
        cancelDeviceReboot,
        cancelDevicePowerOff
    ]);

    function openModal(setter: (open: boolean) => void) {
        popoverRef.current?.close();
        setter(true);
    }

    function confirm(opts: {
        title: string;
        confirmText: string;
        confirmVariant: 'primary' | 'danger';
        run: () => Promise<unknown>;
    }) {
        popoverRef.current?.close();
        confirmationModal.open({
            title: opts.title,
            confirmText: opts.confirmText,
            confirmVariant: opts.confirmVariant,
            cancelText: t('shared.labels.cancel'),
            onConfirm: () => {
                confirmationModal.setLoading(true);
                opts.run()
                    .catch(() => {}) // Error handled in the action
                    .finally(() => confirmationModal.close());
            },
            onCancel: () => confirmationModal.close()
        });
    }

    return (
        <>
            <UIPopover
                ref={popoverRef}
                placement="bottom_left"
                popoverPadding={4}
                trigger="click"
                triggerNode={
                    <button className={styles.removeButtonStyle}>
                        <UIPressableAreaButtonLess width={16} height={16}>
                            <UIIcon
                                name={'more-vertical'}
                                size={16}
                                color={charcoal}
                            />
                        </UIPressableAreaButtonLess>
                    </button>
                }
            >
                <UIMenu>
                    <UIMenuItem
                        text={t('shared.labels.update')}
                        leftIconName="edit-2"
                        onClick={() => {
                            popoverRef.current?.close();
                            onUpdate();
                        }}
                    />
                    <UIMenuItem
                        text={t('shared.devices.test_connectivity_label')}
                        leftIconName="activity"
                        onClick={() => openModal(setIsTestOpen)}
                    />
                    <UIMenuDivider />
                    <UIMenuItem
                        text={t('shared.devices.fall_short_label')}
                        leftImageProps={{
                            src: fallIcon,
                            alt: t('shared.devices.fall_short_label'),
                            width: 18
                        }}
                        onClick={() => openModal(setIsFallOpen)}
                        {...featureLockProps(
                            licenses.fall,
                            t('shared.devices.fall_unavailable')
                        )}
                    />
                    <UIMenuItem
                        text={t('shared.devices.tilt_short_label')}
                        leftImageProps={{
                            src: tiltIcon,
                            alt: t('shared.devices.tilt_short_label'),
                            width: 18
                        }}
                        onClick={() => openModal(setIsTiltOpen)}
                        {...featureLockProps(
                            licenses.tilt,
                            t('shared.devices.tilt_unavailable')
                        )}
                    />
                    <UIMenuItem
                        text={t('shared.devices.geofence_short_label')}
                        leftImageProps={{
                            src: geofenceIcon,
                            alt: t('shared.devices.geofence_short_label'),
                            width: 18
                        }}
                        onClick={() => openModal(setIsGeoOpen)}
                        {...featureLockProps(
                            licenses.geo,
                            t('shared.devices.geofence_unavailable')
                        )}
                    />
                    <UIMenuItem
                        text={t('shared.devices.wifi_label')}
                        leftIconName="wifi"
                        onClick={() => openModal(setIsWifiOpen)}
                    />
                    {cancelEntries.length > 0 && (
                        <>
                            <UIMenuDivider />
                            {cancelEntries.map((entry) => (
                                <UIMenuItem
                                    key={entry.key}
                                    text={entry.label}
                                    leftIconName="x"
                                    onClick={() =>
                                        confirm({
                                            title: t(
                                                'shared.devices.cancel_pending_action_confirm_title'
                                            ),
                                            confirmText: t(
                                                'shared.devices.cancel_pending_action_confirm'
                                            ),
                                            confirmVariant: 'danger',
                                            run: entry.run
                                        })
                                    }
                                />
                            ))}
                        </>
                    )}
                    <UIMenuDivider />
                    <UIMenuItem
                        text={t('shared.devices.reboot_label')}
                        leftIconName="refresh-cw"
                        onClick={() =>
                            confirm({
                                title: t('shared.devices.reboot_confirm_title'),
                                confirmText: t('shared.devices.reboot_label'),
                                confirmVariant: 'primary',
                                run: rebootDevice
                            })
                        }
                    />
                    <UIMenuItem
                        text={t('shared.devices.power_off_label')}
                        leftIconName="power"
                        textColor={red}
                        onClick={() =>
                            confirm({
                                title: t(
                                    'shared.devices.power_off_confirm_title'
                                ),
                                confirmText: t(
                                    'shared.devices.power_off_label'
                                ),
                                confirmVariant: 'danger',
                                run: powerOffDevice
                            })
                        }
                    />
                </UIMenu>
            </UIPopover>
            <TestCallDeviceModal
                deviceId={deviceId}
                isOpen={isTestOpen}
                onClose={() => setIsTestOpen(false)}
            />
            <FallAlertModal
                device={device}
                isOpen={isFallOpen}
                onClose={() => setIsFallOpen(false)}
            />
            <TiltAlertModal
                device={device}
                isOpen={isTiltOpen}
                onClose={() => setIsTiltOpen(false)}
            />
            <BulkGeofenceModal
                companyId={companyId}
                deviceIds={deviceIds}
                isOpen={isGeoOpen}
                onClose={() => setIsGeoOpen(false)}
                onApplied={() => setIsGeoOpen(false)}
            />
            <BulkWifiModal
                companyId={companyId}
                deviceIds={deviceIds}
                isOpen={isWifiOpen}
                onClose={() => setIsWifiOpen(false)}
                onApplied={() => setIsWifiOpen(false)}
            />
        </>
    );
}
